import React, { useEffect, useState, useRef, useCallback } from 'react';
import { PlusSignIcon, Search01Icon, FilterVerticalIcon } from 'hugeicons-react';
import { PrimaryButton, SecondaryButton } from '../../components/forms/buttons';
import { Delete01Icon, PencilEdit02Icon } from 'hugeicons-react';
import AddTargetPopup from '../../components/targets/AddTargetPopup';
import DeleteTargetPopup from '../../components/targets/DeleteTargetPopup';
import EditTarget from '../../components/targets/EditTarget';
import DashboardContainer from '../../components/dashboard/DashboardContainer';
import '../../assets/css/targets/targets.css';
import api, { API_URL } from '../../api';
import { TextInput } from '../../components/forms/Input';
import TableLoading from '../../components/loading/TableLoading';
import { hasRole } from '../../services/userPosition';
import { toast } from 'react-toastify';
import EmptyState from '../../components/common/EmptyState';

const SearchComponent = ({ searchQuery, handleSearch }) => (
  <div className="search-input">
    <TextInput
      iconClass={<Search01Icon />}
      type="search"
      placeholder="Search by measure name"
      value={searchQuery}
      setValue={handleSearch}
    />
  </div>
);

function isValidSearch(query) {
  return query.trim().length > 2;
}

const TargetPageContent = () => {
  const [targets, setTargets] = useState([]);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState(false);
  const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
  const [filters, setFilters] = useState({});
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterPopupRef = useRef(null);
  // Removed all hardcoded year state, only use years from backend
  const [filterMeasure, setFilterMeasure] = useState('');
  const [filterYear, setFilterYear] = useState(null);
  const [filterValueCondition, setFilterValueCondition] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [measureOptions, setMeasureOptions] = useState([]);
  const [yearOptions, setYearOptions] = useState([]);
  const [valueConditionOptions, setValueConditionOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [years, setYears] = useState([]);
  const yearsFetchedRef = useRef(false);
  const initialLoadRef = useRef(true);

  // Fetch years only from backend, never fallback to hardcoded years
  const fetchYears = useCallback(async () => {
    if (yearsFetchedRef.current) return null; // Prevent double fetching

    try {
      const response = await api.get(`${API_URL}/measures/years/`);
      let yearsData;
      if (Array.isArray(response.data)) {
        yearsData = response.data;
      } else if (response.data && response.data.years) {
        yearsData = response.data.years;
      } else if (response.data && response.data.results) {
        yearsData = response.data.results;
      } else {
        yearsData = [];
      }
      const validYears = yearsData
        .map(year => parseInt(year))
        .filter(year => !isNaN(year) && year > 0)
        .sort((a, b) => b - a);
      setYears(validYears);

      yearsFetchedRef.current = true; // Mark as fetched
      return validYears;
    } catch (err) {
      setYears([]); // If backend fails, show no years
      return [];
    }
  }, []);

  const fetchFilterOptions = async (params = {}) => {
    try {
      const response = await api.get(`${API_URL}/targets/`, { params });
      const targetResults = response.data?.results || [];
      const measures = [...new Set(targetResults.map(t => t.measure).filter(Boolean))];
      const valueConditions = [...new Set(targetResults.map(t => t.value_condition).filter(Boolean))];

      const categoryResponse = await api.get(`${API_URL}/categories/list/`);
      const categories = categoryResponse.data || [];

      setMeasureOptions(measures);
      setValueConditionOptions(valueConditions);
      setCategoryOptions(categories.map(cat => cat.name));
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  // Lazy loading - only load data when component is active
  const [dataLoaded, setDataLoaded] = useState(false);

  useEffect(() => {
    if (!dataLoaded) {
      const loadData = async () => {
        setIsLoading(true);

        // Load years first
        const validYears = await fetchYears();

        // Set default filter year after years are loaded
        let defaultYear = null;
        if (validYears && validYears.length > 0) {
          const currentYear = new Date().getFullYear();
          defaultYear = validYears.includes(currentYear) ? currentYear : validYears[0];
          setFilterYear(defaultYear);
        }

        // Fetch targets data immediately with the default year filter
        const initialFilters = defaultYear ? { year: defaultYear } : {};
        await fetchTargetsData(initialFilters);

        setDataLoaded(true);
      };

      loadData();
    }
  }, [dataLoaded, fetchYears]);

  useEffect(() => {
    if (isFilterOpen && dataLoaded) {
      fetchFilterOptions();
    }
  }, [isFilterOpen, dataLoaded]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterPopupRef.current && !filterPopupRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };

    if (isFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isFilterOpen]);


  useEffect(() => {
    const newFilters = {
      ...filters,
      year: filterYear,
      measure_category: filterCategory,
      value_condition: filterValueCondition,
    };
    const cleaned = Object.fromEntries(
      Object.entries(newFilters).filter(([_, v]) => v !== undefined && v !== null && v !== '')
    );
    setFilters(cleaned);
  }, [filterYear, filterCategory, filterValueCondition]);

  const handleFilterButtonClick = (e) => {
    e.stopPropagation();
    setIsFilterOpen((prev) => !prev);
  };

  const handleMeasureChange = (e) => {
    const newMeasure = e.target.value;
    setFilterMeasure(newMeasure);
  };

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setFilterYear(newYear);
  };

  const handleValueConditionChange = (e) => {
    const newValueCondition = e.target.value;
    setFilterValueCondition(newValueCondition);
  };

  const handleCategoryChange = (category) => {
    setFilterCategory(prev => prev === category ? '' : category); // Toggle selection
  };

  const handleResetFilters = () => {
    setFilterMeasure('');
    setFilterYear('');
    setFilterValueCondition('');
    setFilterCategory('');
    setFilters({});
    setIsFilterOpen(false);
    fetchTargetsData();
  };

  // Unified function to fetch targets data
  const fetchTargetsData = async (params = {}, url = null, searchQuery = null) => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      let response;
      const requestParams = {
        ...params,
        ...(searchQuery && { measure: searchQuery })
      };

      if (url) {
        // Extract the path and query parameters from the full URL
        const urlObj = new URL(url);
        const relativePath = urlObj.pathname.substring(4) + urlObj.search;  // Remove '/api' prefix since baseURL includes it
        response = await api.get(relativePath);
      } else {
        response = await api.get(`${API_URL}/targets/`, { params: requestParams });
      }

      let data = [];
      if (response.data && Array.isArray(response.data.results)) {
        data = response.data.results;
        setNextPage(response.data.next);
        setPreviousPage(response.data.previous);
      } else if (Array.isArray(response.data)) {
        data = response.data;
        setNextPage(null);
        setPreviousPage(null);
      } else {
        data = [];
        setNextPage(null);
        setPreviousPage(null);
      }
      setTargets(data);
      if (data.length === 0) {
        setErrorMessage('No targets found. Try changing filters or add new targets.');
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // Page doesn't exist, reset to empty
        setTargets([]);
        setNextPage(null);
        setPreviousPage(null);
        toast.error('No more pages available.');
      } else {
        setTargets([]);
        setNextPage(null);
        setPreviousPage(null);
        setErrorMessage('Failed to fetch targets. Check connection or backend logs.');
      }
    } finally {
      setIsLoading(false);
      setIsOverlayVisible(false);
    }
  };
  const handleChangePage = (url, direction) => {
    fetchTargetsData(filters, url);
    setCurrentPage((prev) => direction === 'next' ? prev + 1 : prev - 1);
  };

  useEffect(() => {
    if (dataLoaded && !initialLoadRef.current) {
      fetchTargetsData(filters);
    } else if (dataLoaded && initialLoadRef.current) {
      initialLoadRef.current = false;
      // Initial load already handled in the dataLoaded effect
    }
  }, [filters, dataLoaded]);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (isValidSearch(query)) {
      setIsSearching(true);
      fetchTargetsData({}, null, query);
    } else if (query.trim() === '') {
      setIsSearching(false);
      fetchTargetsData();
    }
  };


  // Unified handler to refetch targets and years after any add/edit/delete
  const handleDataChanged = () => {
    fetchYears();
    fetchTargetsData(filters);
  };

  const handleAddTargetClick = () => {
    setIsPopupOpen(true);
  };

  const handleApplyFiltersClick = () => {
    const newFilters = {
      measure: filterMeasure,
      year: filterYear,
      value_condition: filterValueCondition,
      measure_category: filterCategory,
    };
    const cleaned = Object.fromEntries(
      Object.entries(newFilters).filter(([_, v]) => v !== undefined && v !== '')
    );
    setFilters(cleaned);
    fetchTargetsData(cleaned);
    setIsFilterOpen(false);
  };

  return (
    <div className="target-page dashboard-page">
      <div className="main-container">
        <div className="buttons-search">
          <div className="search-button">
            <SearchComponent searchQuery={searchQuery} handleSearch={handleSearch} />
          </div>
          <div className='buttons'>
            <SecondaryButton
              className="filter-button"
              onClick={handleFilterButtonClick}
              iconClass={<FilterVerticalIcon />}
              buttonText="Filter"
            />
            {hasRole(['Admin', 'Super User']) && (
              <PrimaryButton
                iconClass={<PlusSignIcon />}
                buttonText="Add Target"
                onClick={handleAddTargetClick}
              />
            )}

       
          </div>
        
        </div>

               {(Object.keys(filters).length > 0) && (
          <div className="filter-summary filter-summary-target">
            <div className="filter-tags">
              {filters.year && (
                <span className="filter-tag">
                  <h3>Year:</h3>
                  <span>{filters.year}</span>
                </span>
              )}
              {filters.measure && (
                <span className="filter-tag">
                  <h3>Measure:</h3>
                  <span>{filters.measure}</span>
                </span>
              )}
              {filters.value_condition && (
                <span className="filter-tag">
                  <h3>Value Condition:</h3>
                  <span>{filters.value_condition}</span>
                </span>
              )}
              {filters.measure_category && (
                <span className="filter-tag">
                  <h3>Category:</h3>
                  <span>{filters.measure_category}</span>
                </span>
              )}
            </div>
            <p className="clear-all-filters" onClick={handleResetFilters}>Clear All</p>
          </div>
        )}

        {isFilterOpen && (
          <div
            className="popup-overlay"
            ref={filterPopupRef}
            onClick={e => {
              if (e.target === filterPopupRef.current) setIsFilterOpen(false);
            }}
          >
            <div
              className="filter-section add-target-popup"
              onClick={e => e.stopPropagation()}
            >
              <div className="filter-item">
                <label>Year</label>
                <select value={filterYear} onChange={handleYearChange} className="year-select">
                  <option value="">Select year</option>
                  {years && years.length > 0 && years.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div className="filter-item">
                <label>Measure</label>
                <select value={filterMeasure} onChange={handleMeasureChange} className="measure-select">
                  <option value="">Select measure</option>
                  {measureOptions.map((measure) => (
                    <option key={measure} value={measure}>{measure}</option>
                  ))}
                </select>
              </div>
              <div className="filter-item">
                <label>Value Condition</label>
                <div className="unit-card-list">
                  {valueConditionOptions.map((cond) => (
                    <div
                      key={cond}
                      className={`card${filterValueCondition === cond ? ' selected' : ''}`}
                      onClick={() => setFilterValueCondition(cond)}
                    >
                      {cond}
                    </div>
                  ))}
                </div>
              </div>
              <div className="filter-item">
                <label>Category</label>
                <div className="card-list">
                  {categoryOptions.map((cat) => (
                    <div
                      key={cat}
                      className={`card${filterCategory === cat ? ' selected' : ''}`}
                      onClick={() => handleCategoryChange(cat)}
                    >
                      {cat}
                    </div>
                  ))}
                </div>
              </div>
              <div className="filter-section">
                <button className="reset-button" onClick={handleResetFilters}>Reset Filters</button>
                <button className="apply-button" onClick={handleApplyFiltersClick}>Close</button>
                
              </div>
            </div>
          </div>
        )}

        {isPopupOpen && (
          <AddTargetPopup
            onClose={() => setIsPopupOpen(false)}
            onTargetAdded={() => {
              setIsPopupOpen(false);
              handleDataChanged();
            }}
          />
        )}

        {isEditPopupOpen && selectedTarget && (
          <EditTarget
            targetData={selectedTarget}
            onClose={() => setIsEditPopupOpen(false)}
            onSubmit={() => {
              setIsEditPopupOpen(false);
              handleDataChanged();
            }}
          />
        )}

        {isDeletePopupOpen && (
          <DeleteTargetPopup
            targetId={selectedTarget}
            targetName={targets.find(t => t.target_id === selectedTarget)?.measure}
            onClose={() => setIsDeletePopupOpen(false)}
            onDelete={() => {
              setIsDeletePopupOpen(false);
              handleDataChanged();
            }}
          />
        )}

        {isLoading ? (
          <TableLoading />
        ) : targets.length === 0 ? (
          <EmptyState
            icon="target"
            title={errorMessage || "No targets found"}
            message={errorMessage || "There are no targets available at the moment."}
            suggestion={errorMessage ? "Try adjusting your filters or add new targets." : "Try changing filters or add new targets."}
          />
        ) : (
          <>
          <div className="targets table-container">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Measure</th>
                  <th>Value</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  {hasRole(['Admin', 'Super User']) && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {targets.map((target) => (
                  <tr key={target.target_id}>
                    <td>{target.target_id}</td>
                    <td>{target.measure}</td>
                    <td>{target.value}</td>
                    <td>{target.starting_date}</td>
                    <td>{target.end_date}</td>
                    {hasRole(['Admin', 'Super User']) && (
                      <td>
                        <div className="action-buttons">
                          <PencilEdit02Icon size={32} className="edit-icon" onClick={() => {
                            setSelectedTarget(target);
                            setIsEditPopupOpen(true);
                          }} />
                          <Delete01Icon size={32} className="delete-icon" onClick={() => {
                            setSelectedTarget(target.target_id);
                            setIsDeletePopupOpen(true);
                          }} />
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pagination-controls">
            <button className={`previous ${!previousPage ? 'disabled' : ''}`} onClick={() => handleChangePage(previousPage, 'prev')} disabled={!previousPage} >Previous</button>
            <span>Page {currentPage}</span>
            <button className={`next ${!nextPage ? 'disabled' : ''}`} onClick={() => handleChangePage(nextPage, 'next')} disabled={!nextPage} >Next</button>
          </div>
          </>
        )}
      </div>
    </div>
  );
};

const TargetPage = () => (
  <DashboardContainer content={<TargetPageContent />} pageTitle="Targets" />
);

export default TargetPage;